import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LessonHeader from "@/components/Activities/LessonHeader";
import ReviewCard from "@/components/Review/ReviewCard";
import { useToast } from "@chakra-ui/react";
import { useSound } from "@/hooks/useSound";
import { useWaterDroplets } from "@/hooks/useWaterDroplets";
import { useReviewCards } from "@/hooks/useReviewCards";

const ReviewPage: React.FC = () => {
  const nav = useNavigate();
  const toast = useToast();
  const once = useRef(false);
  const { playCorrectSound } = useSound();
  const { addDroplets } = useWaterDroplets();
  const [animate, setAnimate] = useState(false);
  const disableCardButtons = useRef(false);

  // Use the review cards hook with InfiniteCardStream
  const {
    currentCard,
    isFlipped,
    handleFlip,
    nextCard,
    markGood,
    markAgain,
    deleteCurrentCard,
  } = useReviewCards({
    onEmpty: () => nav("/home/dictionary"),
    onError: (error) => {
      if (error.message === "Network Error") {
        return;
      }
      const isUpgradeError = error.message?.includes("please upgrade");
      const isCreateAccountError = error.message?.includes(
        "please create an account"
      );
      if (!once.current && !isUpgradeError && !isCreateAccountError) {
        once.current = true;
        toast({
          title: "Error",
          description:
            "I'm sorry we had trouble getting your review cards. Please try again or contact support@fluentwithparrot.com if you have further issues.",
          status: "error",
          duration: 8000,
          isClosable: true,
          position: "top",
        });
      }

      if (isCreateAccountError) {
        nav(`/signup`);
      } else if (isUpgradeError) {
        nav(`/upgrade`);
      } else {
        nav("/home/dictionary");
      }
    },
  });

  const handleAddDrops = (amount: number) => {
    if (amount > 0) {
      setAnimate(true);
      addDroplets(amount);
      setTimeout(() => setAnimate(false), 500);
    }
  };

  const handleReviewGood = async (cardId: number) => {
    // if there is just one card left, we make sure to submit card before fetching again
    if (disableCardButtons.current) {
      return;
    }
    disableCardButtons.current = true;
    try {
      playCorrectSound();
      const result = await markGood(cardId);
      if (result) {
        handleAddDrops(result.waterDropletsAwarded);
      }
    } catch (error) {
      console.error(error);
    } finally {
      await nextCard();
      disableCardButtons.current = false;
    }
  };

  const handleReviewAgain = async (cardId: number) => {
    if (disableCardButtons.current) {
      return;
    }
    disableCardButtons.current = true;
    try {
      await markAgain(cardId);
    } catch (error) {
      console.error(error);
    } finally {
      await nextCard();
      disableCardButtons.current = false;
    }
  };

  const handleDeleteCard = async (cardId: number) => {
    if (disableCardButtons.current) {
      return;
    }
    disableCardButtons.current = true;
    try {
      await deleteCurrentCard(cardId);
    } catch (error) {
      console.error(error);
    } finally {
      await nextCard();
      disableCardButtons.current = false;
    }
  };

  return (
    // one svh is for mobile screens h-full is the backup if svh not supported
    <div className="flex flex-col h-full w-full justify-start items-center bg-black">
      <LessonHeader
        handleBack={() => nav("/home/dictionary")}
        pulse={animate}
      />
      <div className="flex flex-col flex-grow justify-start items-center w-full overflow-hidden">
        <ReviewCard
          card={currentCard}
          isFlipped={isFlipped}
          onTrackListeningTime={() => {}}
          handleFlip={handleFlip}
          handleReviewGood={handleReviewGood}
          handleReviewAgain={handleReviewAgain}
          handleDeleteCard={handleDeleteCard}
        />
      </div>
    </div>
  );
};

export default ReviewPage;
